import React, { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';

import { getOtherUserRole } from 'datamodel/User/utils';
import { DEFAULT_USER_ROLE, SEEKER_USER_ROLE } from 'datamodel/User/constants';
import { fetchUser } from 'datamodel/User/api';

import UserFeedback from 'base/shared/user-feedback';
import { translate } from 'base/shared/Localization';
import { getMoveInDate, getMoveOutDate, getBookingPreviewRaw } from 'datamodel/booking/api';
import { BADI_COLORS } from 'app/badi-components/constants/colors';

import { Spacer, Text, Flex, InlineMessage, MESSAGE_TYPES, Checkbox } from '@badi/badi-components';
import LoadingSpinner from 'components/Icons/LoadingSpinner';
import DatePicker from 'components/Form/DatePicker';
import RoomPreview from 'components/room-preview';
import BookingSummaryCard from 'components/booking-summary-card';
import BookingPhoneCta from 'components/booking-phone-cta';
import BookingStepLayout from '../step-layout';
import { FormWrapper, OutOfFortnightDay, NormalDay } from './styled-components';
import MoveOutPopover from './move-out-popover';
import CalendarLegend from './calendar-legend';
// import FortnightWarning from './fortnight-warning';

import {
  DATE_FORMAT,
  DATE_DISPLAY_FORMAT,
  STATUS,
  STATUS_MESSAGES,
  INPUT_PLACEHOLDER,
} from './constants';
import { isLongStaySelected } from './utils';

const MoveInOutStep = ({
  id,
  moveOutRequired: initialMoveOutRequired,
  connectionId,
  onValidation = () => {},
  user,
  token,
  moveIn: initialMoveIn,
  moveOut: initialMoveOut,
  fortnightMoveIn: initialFortnightMoveIn,
  fornightMoveOut: initialFortnightMoveOut,
  room = null,
  roomId = 0,
  title = '',
  userToInviteId = 0,
  isBadiPlusRoom = false,
}) => {
  const [moveInRange, setMoveInRange] = useState({});
  const [moveOutRange, setMoveOutRange] = useState({});
  const [statusType, setStatus] = useState(STATUS.INITIAL);
  const [otherUser, setOtherUser] = useState(user);
  const [isAnyMoveOut, setIsAnyMoveOut] = useState(initialMoveIn && !initialMoveOut);

  const userRole = otherUser ? getOtherUserRole(otherUser.role) : DEFAULT_USER_ROLE;

  const [moveIn, setMoveIn] = useState(initialMoveIn);
  const [fortnightMoveIn, setFortnightMoveIn] = useState(initialFortnightMoveIn);
  const [moveOut, setMoveOut] = useState(initialMoveOut);
  const [fortnightMoveOut, setFortnightMoveOut] = useState(initialFortnightMoveOut);
  const [showMoveOutPopover, setShowMoveOutPopover] = useState(false);
  const [moveOutRequired, setMoveOutRequired] = useState(false);
  const [showFortnightWarning, setShowFortnightWarning] = useState(false);
  const [popoverClosed, setPopoverClosed] = useState(false);
  const [bookingPreviewData, setBookingPreviewData] = useState(null);
  const [bookingPreviewIsLoading, setBookingPreviewIsLoading] = useState(false);
  const [showSidebarPhoneCta, setShowSidebarPhoneCta] = useState(true);

  // Get the actual roomId, prefer room.id if available, otherwise use the prop
  // Ensure it's a valid number (not NaN, not 0, not undefined/null)
  const actualRoomId = useMemo(() => {
    const id = room?.id || roomId;
    if (!id) return null;
    const numId = Number(id);
    return !isNaN(numId) && numId > 0 ? numId : null;
  }, [room?.id, roomId]);

  const isValidRoomId = actualRoomId !== null;

  useEffect(() => {
    if (userToInviteId) {
      fetchUser(userToInviteId, token)
        .then((userToInvite) =>
          setOtherUser({
            ...userToInvite,
            avatar: userToInvite.picture,
            role: SEEKER_USER_ROLE,
          })
        )
        .catch((error) =>
          UserFeedback.exception(
            'error.invitetobook_notpossible_title',
            'error.invitetobook_notpossible_text',
            error
          )
        );
    }

    if (isBadiPlusRoom) {
      setMoveOutRequired(true);
    }

    // Only proceed if we have either connectionId or valid roomId
    if (!connectionId && !isValidRoomId) return;

    getMoveInDate({ token, connectionId, roomId: actualRoomId }).then(({ data }) => {
      const {
        is_available: isAvailable,
        is_accurate: isAccurate,
        move_in: moveInRangeFromApi,
        fortnight_move_in: fortnightMoveIn,
      } = data;

      if (!isAvailable) {
        setStatus(STATUS.ERROR);
        return;
      }

      if (isAccurate) {
        setStatus(STATUS.OK);
      } else {
        setStatus(STATUS.INACCURATE);
      }

      if (moveInRangeFromApi) {
        const { min_date: minDate, max_date: maxDate } = moveInRangeFromApi;

        setMoveInRange({
          minDate,
          maxDate,
        });
      }

      if (fortnightMoveIn?.length >= 1) {
        let moveInFlattenDays = fortnightMoveIn.flatMap((dates) => [
          moment(dates.min_date.toString()),
          moment(dates.max_date.toString()),
        ]);

        setFortnightMoveIn(moveInFlattenDays);
      }
    });
  }, [connectionId, actualRoomId, userToInviteId, isValidRoomId, token]);

  useEffect(() => {
    if (userToInviteId) {
      return;
    }
    setOtherUser(user);
  }, [user, userToInviteId]);

  useEffect(() => {
    if (!moveIn) return;

    // Only proceed if we have either connectionId or valid roomId
    if (!connectionId && !isValidRoomId) return;

    getMoveOutDate({
      token,
      moveIn,
      roomId: actualRoomId,
      connectionId,
    }).then(({ data }) => {
      if (!data.move_out) return;

      const {
        min_date: minDate,
        max_date: maxDate,
        min_months: minMonths,
        max_months: maxMonths,
        fortnight_move_out: fortnightMoveOut,
      } = data.move_out;

      setMoveOutRange({
        minDate,
        maxDate,
        minMonths,
        maxMonths,
      });

      if (fortnightMoveOut?.length >= 1) {
        let moveOutFlattenDays = fortnightMoveOut.flatMap((dates) => [
          moment(dates.min_date.toString()),
          moment(dates.max_date.toString()),
        ]);

        setFortnightMoveOut(moveOutFlattenDays);
      }
    });
  }, [moveIn, actualRoomId, isValidRoomId]);

  // Call booking preview when both moveIn and moveOut are selected
  useEffect(() => {
    if (moveIn && moveOut && !isAnyMoveOut && (connectionId || isValidRoomId)) {
      setBookingPreviewIsLoading(true);
      setShowSidebarPhoneCta(false);

      getBookingPreviewRaw({
        token,
        roomId: actualRoomId,
        connectionId,
        moveIn,
        moveOut,
      })
        .then((response) => {
          setBookingPreviewData(response.data);
          setBookingPreviewIsLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching booking preview:', error);
          setBookingPreviewData(null);
          setBookingPreviewIsLoading(false);
        });
    } else {
      // Clear preview data when dates are not both selected
      setBookingPreviewData(null);
      setBookingPreviewIsLoading(false);
    }
  }, [moveIn, moveOut, isAnyMoveOut, connectionId, isValidRoomId, actualRoomId, token]);

  // runs as either `moveIn` or `moveOut` changed and call the validation callback
  useEffect(() => {
    if (moveOut && !popoverClosed) {
      const isLongStay = isLongStaySelected({ moveIn, moveOut });

      if (isBadiPlusRoom) {
        setShowMoveOutPopover(false);
      } else if (isLongStay) {
        setShowMoveOutPopover(true);
      } else {
        setShowMoveOutPopover(false);
      }
    }

    onValidation({
      id,
      valid:
        !bookingPreviewIsLoading &&
        !!(isAnyMoveOut || !moveOutRequired ? moveIn : moveIn && moveOut),
      payload: {
        moveIn,
        moveOut: moveOut || '',
      },
    });
  }, [moveIn, moveOut, isAnyMoveOut, bookingPreviewIsLoading]);

  // from here the memoized callbacks
  const handleMoveInDateChange = (moveInSelected) => {
    const date = moveInSelected ? moveInSelected.format(DATE_FORMAT) : null;
    setMoveIn(date);
    setMoveOut(null);
    setShowFortnightWarning(false);
    setShowMoveOutPopover(false);
  };

  const handleMoveOutDateChange = (moveOutSelected) => {
    const date = moveOutSelected.format(DATE_FORMAT);
    setMoveOut(date);
    if (
      isBadiPlusRoom &&
      (!isFortnightMoveInDate(moment(moveIn)) || !isFortnightMoveOutDate(moveOutSelected))
    ) {
      setShowFortnightWarning(true);
    } else {
      setShowFortnightWarning(false);
    }
  };

  const isMoveInOutsideRange = useCallback(
    (day) => !day.isBetween(moveInRange.minDate, moveInRange.maxDate, 'day', '[]'),
    [moveInRange]
  );

  const isMoveOutOutsideRange = useCallback(
    (day) => !day.isBetween(moveOutRange.minDate, moveOutRange.maxDate, 'day', '[]'),
    [moveOutRange]
  );

  const isFortnightMoveInDate = useCallback(
    (day) => {
      if (fortnightMoveIn?.length >= 1) {
        const firstDate = moment(moveInRange.minDate);

        if ((firstDate.date() !== 1 || firstDate.date() !== 15) && day.isSame(firstDate, 'day')) {
          return false;
        } else if (!isMoveInOutsideRange(day) && (day.date() === 1 || day.date() === 15)) {
          return true;
        } else {
          return false;
          // return fortnightMoveIn.some(
          //   (fortnightDay) => day.isSame(fortnightDay, 'day') && !day.isBefore(firstDate, 'day')
          // );
        }
      }
    },
    [fortnightMoveIn]
  );

  const isFortnightMoveOutDate = useCallback(
    (day) => {
      if (fortnightMoveOut?.length >= 1) {
        return fortnightMoveOut.some(
          (fortnightDay) =>
            day.isSame(fortnightDay, 'day') && !day.isBefore(moveOutRange.minDate, 'day')
        );
      }
    },
    [fortnightMoveOut]
  );

  const fortnightInDayContent = useCallback(
    (day) => {
      const dayNumber = day.format('D');

      if (!day.isBetween(moveInRange.minDate, moveInRange.maxDate)) {
        return <NormalDay>{dayNumber}</NormalDay>;
      } else if (
        fortnightMoveIn?.length >= 1 &&
        !fortnightMoveIn.some((d) => d.isSame(day, 'day'))
      ) {
        return <OutOfFortnightDay>{dayNumber}</OutOfFortnightDay>;
      }

      return <NormalDay>{dayNumber}</NormalDay>;
    },
    [fortnightMoveIn, moveInRange]
  );

  const fortnightOutDayContent = useCallback(
    (day) => {
      const dayNumber = day.format('D');

      if (!day.isBetween(moveOutRange.minDate, moveOutRange.maxDate)) {
        return <NormalDay>{dayNumber}</NormalDay>;
      } else if (
        fortnightMoveOut?.length >= 1 &&
        !fortnightMoveOut.some((d) => d.isSame(day, 'day'))
      ) {
        return <OutOfFortnightDay>{dayNumber}</OutOfFortnightDay>;
      }

      return <NormalDay>{dayNumber}</NormalDay>;
    },
    [fortnightMoveOut, moveOutRange]
  );

  const initialMoveInVisibleMonth = useCallback(
    () => (moveIn ? moment(moveIn) : moment(moveInRange.minDate)),
    [moveInRange, moveIn]
  );

  const initialMoveOutVisibleMonth = useCallback(
    () => (moveOut ? moment(moveOut) : moment(moveOutRange.minDate)),
    [moveOutRange, moveOut]
  );

  const handleMoveOutClose = () => {
    if (showMoveOutPopover) {
      setShowMoveOutPopover(false);
      setPopoverClosed(true);
    }
  };

  const handleAnyMoveout = () => {
    setIsAnyMoveOut(!isAnyMoveOut);
    setMoveOut(null);
  };

  const sidebar = [];

  if (otherUser?.id && room) {
    sidebar.push(
      <Spacer>
        <RoomPreview
          key="booking-room-preview"
          room={room}
          showRoomDetails={false}
          showExternalAgencyId={false}
          showUserDetails={false}
          token={token}
          offset={2}
        />
        {!!showSidebarPhoneCta && (
          <Spacer id="sidebar-phone-cta" top={3}>
            <BookingPhoneCta />
          </Spacer>
        )}
      </Spacer>
    );
  }

  return (
    <BookingStepLayout
      hideBadiGuaranteeCard={true}
      sidebar={sidebar}
      title={translate(title)}
      userRole={userRole}
    >
      <FormWrapper>
        <Flex direction="column" gutter={3}>
          <label htmlFor="move-in">
            <Spacer bottom={1}>
              <Text body={2}>{translate('booking.new.movein_label')}</Text>
            </Spacer>
            <DatePicker
              displayFormat={DATE_DISPLAY_FORMAT}
              id="move-in"
              initialVisibleMonth={initialMoveInVisibleMonth}
              isOutsideRange={isMoveInOutsideRange}
              renderDayContents={fortnightInDayContent}
              calendarInfoPosition="top"
              name="move-in"
              onChange={handleMoveInDateChange}
              placeholder={moveIn || INPUT_PLACEHOLDER[statusType]}
              value={moveIn && moment(moveIn)}
            />
          </label>
          {!isBadiPlusRoom && statusType === STATUS.INACCURATE && (
            <InlineMessage data-qa="tip-card-warning-status" variant={MESSAGE_TYPES.WARNING}>
              <Text body={3}>{translate(STATUS_MESSAGES[STATUS.INACCURATE])}</Text>
            </InlineMessage>
          )}
          <label htmlFor="move-out">
            <Spacer bottom={1}>
              <Text body={2}>
                {translate(
                  `booking.new.moveout_label.${moveOutRequired ? 'required' : 'optional'}`
                )}
              </Text>
            </Spacer>
            {!isBadiPlusRoom && (
              <Spacer bottom={2}>
                <Checkbox
                  checked={isAnyMoveOut}
                  data-qa="any-move-out-checkbox"
                  id="any-move-out"
                  name="any-move-out"
                  onChange={handleAnyMoveout}
                >
                  <Text body="12-Medium">
                    {translate('booking.new.any_moveout_checkbox_label')}
                  </Text>
                </Checkbox>
              </Spacer>
            )}
            <DatePicker
              disabled={!moveIn || isAnyMoveOut}
              displayFormat={DATE_DISPLAY_FORMAT}
              id="move-out"
              initialVisibleMonth={initialMoveOutVisibleMonth}
              isOutsideRange={isMoveOutOutsideRange}
              name="move-out"
              renderDayContents={fortnightOutDayContent}
              calendarInfoPosition="top"
              onChange={handleMoveOutDateChange}
              onClose={handleMoveOutClose}
              placeholder={moveOut || INPUT_PLACEHOLDER[statusType]}
              value={moveOut && moment(moveOut)}
            />
            {showMoveOutPopover && !popoverClosed && <MoveOutPopover />}
          </label>
          {/* temporarily fortnightwarning */}
          {/* showFortnightWarning && (
            <FortnightWarning
              moveIn={moveIn}
              moveOut={moveOut}
              isFortnightMoveInDate={isFortnightMoveInDate}
              isFortnightMoveOutDate={isFortnightMoveOutDate}
              fortnightMoveIn={fortnightMoveIn}
              fortnightMoveOut={fortnightMoveOut}
            />
          ) */}
          {statusType === STATUS.ERROR && (
            <InlineMessage data-qa="tip-card-request-status" variant={MESSAGE_TYPES.ERROR}>
              <Text body={3}>{translate(STATUS_MESSAGES[STATUS.ERROR])}</Text>
            </InlineMessage>
          )}

          {bookingPreviewIsLoading && (
            <Spacer top={2}>
              <Flex alignItems="center" direction="column">
                <LoadingSpinner color={BADI_COLORS.BRAND.PRIMARY.DEFAULT} />
              </Flex>
            </Spacer>
          )}

          {!bookingPreviewIsLoading && bookingPreviewData?.seeker_pricing?.payments_breakdown && (
            <Spacer top={2}>
              <BookingSummaryCard
                paymentsBreakdown={bookingPreviewData.seeker_pricing.payments_breakdown}
              />
            </Spacer>
          )}
        </Flex>
      </FormWrapper>
    </BookingStepLayout>
  );
};

export default MoveInOutStep;
